/**
 * A4Sheet.js
 *
 * Renders a miniature A4 paper page with note tiles.
 * Supports dynamic 8-card target capacity (2 columns × 4 rows) or 6-card fallback.
 */
import React, { forwardRef, useState } from 'react';
import { AnimatePresence, motion, LayoutGroup } from 'framer-motion';
import { CompactNoteBlock, ExpandedNoteBlock } from './NoteBlock';
import styles from './A4Sheet.module.css';

const A4Sheet = forwardRef(function A4Sheet(
  { pageIndex, notes, onDelete, onSave, isLatest, landingNoteId, onPaperClick, isUnlocked, onLockedAction, subjectTitle = 'Study' },
  ref
) {
  const [expandedNote, setExpandedNote] = useState(null);

  // Target capacity: 8 if page has >6 notes, otherwise 6 (or 8 for layout stability)
  const targetCapacity = notes.length > 6 ? 8 : 6;

  // Fill grid to target capacity
  const filledSlots = [...notes];
  while (filledSlots.length < targetCapacity) filledSlots.push(null);

  const isEmpty = notes.length === 0;

  const handleExpand = (note) => setExpandedNote(note);

  const handleSave = (id, fields) => {
    onSave(id, fields);
    setExpandedNote(null);
  };

  const handleDelete = (id) => {
    onDelete(id);
    setExpandedNote(null);
  };

  return (
    <LayoutGroup id={`page-${pageIndex}`}>
      <motion.div
        className={styles.paperWrap}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 24, delay: pageIndex * 0.08 }}
      >
        {/* ── Miniature paper container ─────────────────────────────────── */}
        <div
          className={`${styles.paper} ${targetCapacity === 8 ? styles.paper8Slots : ''}`}
          ref={ref}
          onClick={() => onPaperClick?.(pageIndex)}
          title="Click to zoom page view"
        >
          {/* Decorative ruled header */}
          <div className={styles.paperHeader}>
            <span className={styles.paperHeaderTitle}>{subjectTitle} Notes</span>
            <span className={styles.zoomHintBtn}>🔍 Zoom</span>
          </div>

          {isEmpty ? (
            <div className={styles.emptyPaper}>
              <span className={styles.emptyPaperIcon}>📄</span>
              <span className={styles.emptyPaperText}>Empty page</span>
            </div>
          ) : (
            <div className={`${styles.noteGrid} ${targetCapacity === 8 ? styles.noteGrid8 : ''}`}>
              {filledSlots.map((note, i) =>
                note ? (
                  <CompactNoteBlock
                    key={note.id}
                    note={note}
                    onExpand={handleExpand}
                    onDelete={isUnlocked ? handleDelete : onLockedAction}
                    isLanding={note.id === landingNoteId}
                  />
                ) : (
                  <div key={`empty-${i}`} className={styles.emptySlot} />
                )
              )}
            </div>
          )}
        </div>

        {/* Page badge */}
        <div className={`${styles.pageBadge} ${isLatest ? styles.pageBadgeActive : ''}`}>
          Page {pageIndex + 1}
          {isLatest && notes.length < targetCapacity && (
            <span style={{ opacity: 0.7, fontWeight: 400 }}>
              {' '}· {targetCapacity - notes.length} left
            </span>
          )}
        </div>
      </motion.div>

      {/* ── Full-A4 expanded note overlay ────────────────────────────── */}
      <AnimatePresence>
        {expandedNote && (
          <ExpandedNoteBlock
            key={expandedNote.id}
            note={expandedNote}
            pageIndex={pageIndex}
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={() => setExpandedNote(null)}
            isUnlocked={isUnlocked}
            onLockedAction={onLockedAction}
            subjectTitle={subjectTitle}
          />
        )}
      </AnimatePresence>
    </LayoutGroup>
  );
});

export default A4Sheet;
